import produce from "immer";
import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { v4 as uuid } from "uuid";

import { useGetCustomFormValuesQuery } from "@/apollo/types";
import { SecondaryButton } from "@/components/elements/Button";
import FieldLabel from "@/components/primitives/InputLabel";
import { DropdownOption, FormType } from "@/integrations";

import FormFactory from "../FormFactory";
import GorgiasCustomReport from "./GorgiasCustomReport";
import { snakeCase } from "lodash";

export type GorgiasReport = {
  id: string;
  name: string;
  scope?: DropdownOption;
  measures?: DropdownOption[];
  dimensions?: DropdownOption[];
  timeDimension?: DropdownOption;
  granularity?: DropdownOption;
};

export type GorgiasScopeGroup = {
  scope: DropdownOption;
  measures: DropdownOption[];
  dimensions: DropdownOption[];
  timeDimensions: DropdownOption[];
  granularities: DropdownOption[];
};

/**
 * Pure local validation for a Gorgias report. Returns an error message
 * when the report is not yet ready to be sent to the backend, or `null`
 * when it is locally valid.
 *
 * This mirrors the most important required-field checks from the backend
 * `validateCustomForm` so that we can gate side-effects (like refetching
 * streams/schemas) on locally complete reports.
 */
export const isReportLocallyValid = (r: GorgiasReport): string | null => {
  if (!r.name) return "Report name is required";
  if (!r.scope?.value) return "Report scope is required";
  if (!r.measures?.length) return "At least one measure is required";
  if (!r.timeDimension?.value) return "Time dimension is required";
  if (!r.granularity?.value) return "Granularity is required";
  return null;
};

const useCustomFormFieldsQuery = (connectionId: string) => {
  const { data, loading, refetch } = useGetCustomFormValuesQuery({
    variables: {
      input: {
        connectionId: connectionId,
        dependencies: "{}",
      },
    },
    fetchPolicy: "network-only",
  });
  return {
    data: data?.getCustomFormValues.customFormValues as
      { scopes?: GorgiasScopeGroup[] } | undefined,
    loading,
    refetch,
  };
};

export default function GorgiasForm(props: {
  field: Extract<FormType, { type: "gorgias-form" }>;
}) {
  const context = useFormContext();
  const connectionId = context.getValues("connectionId");

  const [reportErrors, setReportErrors] = useState<Record<string, string>>({});
  const [invalidReportIds, setInvalidReportIds] = useState<string[]>([]);

  const { data: customFieldsData } = useCustomFormFieldsQuery(connectionId);

  useEffect(() => {
    context.register("reports", { required: false });
  }, [context]);

  const getInitialReports = () => {
    const values: GorgiasReport[] = context.getValues("reports");
    return values?.filter((v) => !!v) ?? [];
  };
  const [reports, setReports] = useState<GorgiasReport[]>(getInitialReports());

  const changeReport = (report: GorgiasReport) => {
    setReports((prev) =>
      produce(prev, (draft) => {
        const cur = draft.find((rep) => rep.id === report.id);
        if (cur) {
          cur.name = report.name;
          cur.scope = report.scope;
          cur.measures = report.measures;
          cur.dimensions = report.dimensions;
          cur.timeDimension = report.timeDimension;
          cur.granularity = report.granularity;
        }
      }),
    );
  };

  const removeReport = (report: GorgiasReport) => {
    setReports((prev) =>
      produce(prev, (draft) => {
        draft = draft.filter((rep) => rep.id !== report.id);
        return draft;
      }),
    );
    setInvalidReportIds((prev) => prev.filter((x) => x !== report.id));
  };

  const onReportInvalid = useCallback((reportId: string) => {
    setInvalidReportIds((prev) =>
      prev.includes(reportId) ? prev : [...prev, reportId],
    );
  }, []);

  const onReportValid = useCallback((reportId: string) => {
    setInvalidReportIds((prev) => prev.filter((x) => x !== reportId));
  }, []);

  const setValue = context.setValue;
  const setError = context.setError;

  useEffect(() => {
    if (reports.length === 0) {
      setValue("reports", [], {
        shouldValidate: false,
      });
    }
  }, [reports.length, setValue]);

  useEffect(() => {
    const currentReports = reports;
    if (!Array.isArray(currentReports)) return;

    // Only propagate the reports value to react-hook-form (and therefore
    // trigger the downstream Schemas/GetStreams refetch) when every report
    // passes local validation. Server-side validation failures are surfaced
    // separately via the per-report Validate button.
    const allLocallyValid = currentReports.every(
      (r) => isReportLocallyValid(r) === null,
    );

    const duplicateReportNames = currentReports.filter(
      (x) =>
        currentReports.filter((y) => snakeCase(y.name) === snakeCase(x.name))
          .length > 1,
    );

    const _reportErrors: Record<string, string> = {};
    for (const duplicateReport of duplicateReportNames) {
      _reportErrors[duplicateReport.id] = "Report names must be unique";
    }
    setReportErrors(_reportErrors);

    if (
      !allLocallyValid ||
      duplicateReportNames.length > 0 ||
      invalidReportIds.length > 0
    ) {
      setError("reports", {
        message: "Validation error",
      });
      return;
    }

    setValue("reports", currentReports, {
      shouldValidate: true,
    });
  }, [reports, setValue, setError, invalidReportIds.length]);

  const scopeGroups = customFieldsData?.scopes ?? [];

  return (
    <div className="space-y-8">
      {props.field.fields?.map((f) => (
        <FormFactory key={f.name} form={f} />
      ))}
      {!!reports.length && scopeGroups.length > 0 && (
        <div>
          <FieldLabel>Custom reports ({reports.length}/20)</FieldLabel>
          <div className="flex flex-col gap-4">
            {reports.map((r) => {
              return (
                <GorgiasCustomReport
                  report={r}
                  key={r.id}
                  onChangeReport={changeReport}
                  onRemoveReport={removeReport}
                  validationError={reportErrors[r.id]}
                  connectionId={connectionId}
                  scopeGroups={scopeGroups}
                  onReportInvalid={onReportInvalid}
                  onReportValid={onReportValid}
                />
              );
            })}
          </div>
        </div>
      )}
      <SecondaryButton
        onClick={() => {
          const id = uuid();
          setReports((prev) => [
            ...prev,
            {
              id,
              name: "",
              scope: undefined,
              measures: [],
              dimensions: [],
              timeDimension: undefined,
              granularity: undefined,
            },
          ]);
          setInvalidReportIds((prev) => [...prev, id]);
        }}
      >
        Add custom report
      </SecondaryButton>
    </div>
  );
}
